import React, { useState, useEffect } from 'react';

const AssetsManagement = ({
  assets = [],
  user = {},
  assignableUsers = [],
  handleAssetClick = () => {},
  handleCreateAsset = () => {},
  handleEditAsset = () => {},
  handleDeleteAsset = () => {},
  handleAssignAsset = () => {},
  downloadFile = () => {},
  API_URL = '',
  assetForm = { name: '', description: '', asset_type: 'laptop', value: 0, serial_number: '' },
  setAssetForm = () => {},
  editingAsset = null,
  setEditingAsset = () => {},
  editAssetForm = { name: '', description: '', asset_type: '', value: 0, serial_number: '', assigned_to: '' },
  setEditAssetForm = () => {},
  initialOpenCreate = false
}) => {
  const [showCreateModal, setShowCreateModal] = useState(initialOpenCreate);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    if (initialOpenCreate && (user.role === 'admin' || user.role === 'superadmin')) {
      setShowCreateModal(true);
    }
  }, [initialOpenCreate, user.role]);

  const isAdmin = user.role === 'admin' || user.role === 'superadmin';

  // Metrics
  const totalAssets = assets.length;
  const assignedCount = assets.filter(a => a.assigned_to).length;
  const availableCount = assets.filter(a => !a.assigned_to).length;
  const totalValue = assets.reduce((sum, a) => sum + (parseFloat(a.value) || 0), 0);

  // Filtering & Sorting
  const filteredAssets = assets
    .filter(asset => {
      // Search
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesName = (asset.name || '').toLowerCase().includes(query);
        const matchesDesc = (asset.description || '').toLowerCase().includes(query);
        const matchesSerial = (asset.serial_number || '').toLowerCase().includes(query);
        const matchesType = (asset.asset_type || '').toLowerCase().includes(query);
        const matchesUser = (asset.assigned_to_username || '').toLowerCase().includes(query);
        if (!matchesName && !matchesDesc && !matchesSerial && !matchesType && !matchesUser) {
          return false;
        }
      }

      // Type filter
      if (typeFilter && asset.asset_type !== typeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter === 'assigned' && !asset.assigned_to) return false;
      if (statusFilter === 'available' && asset.assigned_to) return false;

      // User filter
      if (userFilter && String(asset.assigned_to) !== String(userFilter)) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'value') {
        comparison = (parseFloat(a.value) || 0) - (parseFloat(b.value) || 0);
      } else if (sortBy === 'type') {
        comparison = (a.asset_type || '').localeCompare(b.asset_type || '');
      } else if (sortBy === 'created_at') {
        comparison = new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const resetFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setStatusFilter('');
    setUserFilter('');
    setSortBy('name');
    setSortOrder('asc');
  };

  const getAssetIcon = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'laptop':
        return (
          <svg className="w-4 h-4 text-purple-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'desktop':
        return (
          <svg className="w-4 h-4 text-blue-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'mobile':
        return (
          <svg className="w-4 h-4 text-emerald-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'monitor':
        return (
          <svg className="w-4 h-4 text-indigo-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'printer':
        return (
          <svg className="w-4 h-4 text-amber-600 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-slate-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
    }
  };

  const onCreateSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleCreateAsset(e);
      setShowCreateModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleEditAsset(e);
      setEditingAsset(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 pb-12">
      {/* 1. Static / Sticky Top Control Deck */}
      <div className="sticky top-0 z-20 bg-[#F4F7FB]/95 backdrop-blur-md pt-0.5 pb-2 space-y-2.5">
        {/* Header + Actions */}
        <div className="bg-white rounded-2xl p-4 md:px-5 md:py-3.5 border border-slate-200/80 shadow-xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Left: Title & Subtitle */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-xs flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base md:text-xl font-bold text-slate-900 tracking-tight">Equipment Fleet</h1>
                <span className="bg-emerald-50 text-emerald-700 font-bold text-[10px] md:text-[11px] px-2 py-0.5 rounded-full border border-emerald-200">
                  {totalAssets} Total
                </span>
                {!isAdmin && (
                  <span className="bg-slate-50 text-slate-500 border border-slate-200 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    Personal View
                  </span>
                )}
              </div>
              <p className="text-[10px] md:text-[11px] text-slate-400 font-medium">Hardware inventory, valuation, and user assignments</p>
            </div>
          </div>

          {/* Center: KPI Pills (Desktop) */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/70 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span>Assigned:</span>
              <span className="font-bold text-slate-900">{assignedCount}</span>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/70 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700">
              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
              <span>Available:</span>
              <span className="font-bold text-slate-900">{availableCount}</span>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/70 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                <span>Total Capex:</span>
                <span className="font-bold text-slate-900">₹{totalValue.toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 justify-between sm:justify-end">
            {/* Export Dropdown */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  downloadFile(`${API_URL}/api/export/assets?format=${e.target.value}`, `assets.${e.target.value}`);
                  e.target.value = '';
                }
              }}
              className="bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold px-2.5 md:px-3 py-1.5 md:py-2 rounded-xl cursor-pointer transition-colors shadow-2xs focus:outline-none"
            >
              <option value="">Export Data</option>
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
              <option value="pdf">PDF</option>
              <option value="json">JSON</option>
            </select>

            {/* Primary Add Asset Button (Admin Only) */}
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-tr from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:scale-95 text-white text-xs font-bold px-3 md:px-3.5 py-1.5 md:py-2 rounded-xl shadow-xs shadow-emerald-200 flex items-center gap-1.5 transition-all duration-200 group"
              >
                <svg className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Asset</span>
              </button>
            )}
          </div>
        </div>

        {/* 2. Compact Modern Filter & Search Bar + Mobile Chips */}
        <div className="bg-white rounded-2xl p-2.5 md:p-3 border border-slate-200/80 shadow-xs space-y-2">
          {/* Mobile Horizontal Filter Chips */}
          <div className="flex sm:hidden items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
            <button
              onClick={() => {
                setStatusFilter('');
                setTypeFilter('');
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                !statusFilter && !typeFilter
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                  : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}
            >
              All ({totalAssets})
            </button>
            <button
              onClick={() => setStatusFilter('assigned')}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                statusFilter === 'assigned'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}
            >
              Assigned ({assignedCount})
            </button>
            <button
              onClick={() => setStatusFilter('available')}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                statusFilter === 'available'
                  ? 'bg-slate-700 text-white border-slate-700 shadow-2xs'
                  : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              Available ({availableCount})
            </button>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 md:gap-2.5">
            {/* Quick Search */}
            <div className="relative flex-1 min-w-[180px]">
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search asset, serial number, user..."
                className="w-full bg-slate-50 border border-slate-200 focus:border-emerald-500 focus:bg-white rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              {/* Asset Type Filter */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-purple-500 min-w-[110px]"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Type: All</option>
                <option value="laptop">Laptop</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile Device</option>
                <option value="monitor">Display Monitor</option>
                <option value="printer">Printer</option>
                <option value="other">Other Asset</option>
              </select>

              {/* Status Filter */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-purple-500 min-w-[100px]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Status: All</option>
                <option value="assigned">Assigned</option>
                <option value="available">Available</option>
              </select>

              {/* Assigned User Filter (Admin Only) */}
              {isAdmin && (
                <select
                  className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-purple-500 min-w-[120px] max-w-[140px] truncate"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                >
                  <option value="">User: All</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))}
                </select>
              )}

              {/* Sort Filter */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-purple-500 min-w-[100px]"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="value">Value (₹)</option>
                <option value="type">Type</option>
                <option value="created_at">Date Added</option>
              </select>

              {/* Sort Order */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-purple-500 min-w-[80px]"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>

              {/* Reset Button */}
              <button
                onClick={resetFilters}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-800 border border-slate-200 rounded-xl transition-colors shadow-2xs"
                title="Reset all filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              {/* View Mode Toggle */}
              <div className="bg-slate-100 p-0.5 rounded-xl flex items-center gap-0.5">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${
                    viewMode === 'table' ? 'bg-white text-purple-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  title="Table View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${
                    viewMode === 'grid' ? 'bg-white text-purple-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  title="Cards View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Assets Content (Table or Cards View) */}
      {/* Mobile View: High-Density Material 3 Asset Cards */}
      <div className="block md:hidden space-y-2.5">
        {filteredAssets.map((asset) => (
          <div
            key={asset.id}
            onClick={() => handleAssetClick(asset)}
            className="bg-white rounded-2xl p-3.5 border border-slate-200/90 shadow-2xs active:scale-[0.99] transition-all space-y-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center text-base shadow-2xs flex-shrink-0 border border-emerald-100">
                  {getAssetIcon(asset.asset_type)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 text-xs truncate">
                    {asset.name}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded">
                      {asset.asset_type || 'Device'}
                    </span>
                    {asset.serial_number && (
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-50 px-1.5 py-0.2 rounded border border-slate-200">
                        S/N: {asset.serial_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <div className="font-extrabold text-slate-900 text-xs">
                  ₹{parseFloat(asset.value || 0).toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            {/* Status & Assignment */}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px]">
              {asset.assigned_to ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{asset.assigned_to_username || 'Assigned'}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                  Available Fleet
                </span>
              )}

              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingAsset(asset);
                    setEditAssetForm({
                      name: asset.name || '',
                      description: asset.description || '',
                      asset_type: asset.asset_type || '',
                      value: asset.value || 0,
                      serial_number: asset.serial_number || '',
                      assigned_to: asset.assigned_to || ''
                    });
                  }}
                  className="px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg active:scale-95 transition-all"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View: Table / Grid */}
      {viewMode === 'table' ? (
        <div className="hidden md:block bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100">
                <tr>
                  <th className="py-2.5 px-4 md:px-5">Asset & Identification</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Value</th>
                  <th className="py-2.5 px-3">Status / Assigned User</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAssets.map((asset) => (
                  <tr
                    key={asset.id}
                    onClick={() => handleAssetClick(asset)}
                    className="hover:bg-slate-50/70 transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-4 md:px-5">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center text-sm shadow-2xs flex-shrink-0">
                          {getAssetIcon(asset.asset_type)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-slate-800 group-hover:text-purple-700 transition-colors truncate">
                              {asset.name}
                            </span>
                            {asset.serial_number && (
                              <span className="text-[10px] font-mono font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200/60">
                                S/N: {asset.serial_number}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 truncate mt-0.5 max-w-sm md:max-w-md">
                            {asset.description || 'No description provided'}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize bg-slate-100 text-slate-700 border border-slate-200/60">
                        {asset.asset_type || 'Hardware'}
                      </span>
                    </td>

                    <td className="py-3 px-3 font-bold text-slate-800 text-xs">
                      ₹{parseFloat(asset.value || 0).toLocaleString('en-IN')}
                    </td>

                    <td className="py-3 px-3">
                      {asset.assigned_to ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Assigned: {asset.assigned_to_username || 'User'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500 border border-slate-200/60">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                          Available
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {isAdmin && !asset.assigned_to && (
                          <select
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAssignAsset(asset.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="text-[11px] border border-slate-200 rounded-lg px-2 py-1 bg-slate-50 hover:bg-slate-100 text-slate-700 focus:outline-none focus:border-purple-500"
                          >
                            <option value="">Assign...</option>
                            {assignableUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.username}</option>
                            ))}
                          </select>
                        )}

                        {isAdmin && (
                          <button
                            onClick={() => {
                              setEditingAsset(asset);
                              setEditAssetForm({
                                name: asset.name,
                                description: asset.description,
                                asset_type: asset.asset_type,
                                value: asset.value,
                                serial_number: asset.serial_number || '',
                                assigned_to: asset.assigned_to || ''
                              });
                            }}
                            className="px-2 py-1 text-[11px] font-semibold text-purple-700 hover:text-white hover:bg-purple-600 rounded-lg transition-colors border border-purple-200 hover:border-purple-600"
                          >
                            Edit
                          </button>
                        )}

                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteAsset(asset.id, asset.name)}
                            className="px-2 py-1 text-[11px] font-semibold text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors border border-red-200 hover:border-red-600"
                          >
                            Delete
                          </button>
                        )}

                        {!isAdmin && (
                          <button
                            onClick={() => handleAssetClick(asset)}
                            className="px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-50 rounded-lg border border-purple-200"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAssets.length === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-2.5">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-sm font-bold text-slate-800">No assets found</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isAdmin ? 'No assets match your search/filter criteria.' : 'No assets are currently assigned to your account.'}
              </p>
              {isAdmin && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs px-3.5 py-1.5 rounded-xl shadow-xs"
                >
                  + Add New Asset
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Grid Cards View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredAssets.map((asset) => (
            <div
              key={asset.id}
              onClick={() => handleAssetClick(asset)}
              className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between cursor-pointer group"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="flex items-center">{getAssetIcon(asset.asset_type)}</span>
                    <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                      {asset.asset_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {asset.assigned_to ? (
                      <span className="px-1.5 py-0.2 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Assigned
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.2 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                        Available
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="font-bold text-slate-800 text-xs md:text-sm group-hover:text-purple-700 transition-colors line-clamp-1">
                  {asset.name}
                </h3>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">
                  {asset.description || 'No description provided'}
                </p>

                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900">
                    ₹{parseFloat(asset.value || 0).toLocaleString('en-IN')}
                  </span>
                  {asset.serial_number && (
                    <span className="text-[10px] font-mono text-slate-400">
                      S/N: {asset.serial_number}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <div className="text-[10px] text-slate-400 truncate max-w-[140px]">
                  {asset.assigned_to_username ? `Assigned: ${asset.assigned_to_username}` : 'Unassigned'}
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        setEditingAsset(asset);
                        setEditAssetForm({
                          name: asset.name,
                          description: asset.description,
                          asset_type: asset.asset_type,
                          value: asset.value,
                          serial_number: asset.serial_number || '',
                          assigned_to: asset.assigned_to || ''
                        });
                      }}
                      className="p-1 text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Edit Asset"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteAsset(asset.id, asset.name)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Asset"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 4. Create Asset Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="fixed inset-0" onClick={() => setShowCreateModal(false)} />

          <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 flex flex-col max-h-[92vh] animate-fadeIn">
            <div className="p-4 md:px-5 md:py-4 border-b border-slate-100 flex items-center justify-between bg-purple-600 text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-xs text-white flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-tight">Create New Asset</h3>
                  <p className="text-[11px] text-purple-100">Add hardware or device to inventory</p>
                </div>
              </div>

              <button
                onClick={() => setShowCreateModal(false)}
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-xs"
              >
                ✕
              </button>
            </div>

            <form onSubmit={onCreateSubmit} className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3.5">
              {/* Asset Name */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Asset Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. MacBook Pro 16 M3 Max"
                  className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-2xs"
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Description / Specifications <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Hardware specs, condition, model number, accessories included..."
                  className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl p-3 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-2xs resize-none"
                  value={assetForm.description}
                  onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })}
                />
              </div>

              {/* Asset Type Selectable Cards */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Asset Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'laptop', label: 'Laptop' },
                    { value: 'desktop', label: 'Desktop' },
                    { value: 'mobile', label: 'Mobile' },
                    { value: 'monitor', label: 'Monitor' },
                    { value: 'printer', label: 'Printer' },
                    { value: 'other', label: 'Other' }
                  ].map((t) => (
                    <div
                      key={t.value}
                      onClick={() => setAssetForm({ ...assetForm, asset_type: t.value })}
                      className={`p-2 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-2 ${
                        assetForm.asset_type === t.value
                          ? 'border-purple-600 bg-purple-50/40 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <span className="flex-shrink-0">{getAssetIcon(t.value)}</span>
                      <span className="font-bold text-xs text-slate-800">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value & Serial Number */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Value (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all shadow-2xs"
                    value={assetForm.value}
                    onChange={(e) => setAssetForm({ ...assetForm, value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. C02G1234MD6R"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-2xs font-mono"
                    value={assetForm.serial_number || ''}
                    onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Add to Inventory</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Edit Asset Modal */}
      {editingAsset && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="fixed inset-0" onClick={() => setEditingAsset(null)} />

          <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 flex flex-col max-h-[92vh] animate-fadeIn">
            <div className="p-4 md:px-5 md:py-4 border-b border-slate-100 flex items-center justify-between bg-purple-600 text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-xs text-white flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-tight">Edit Asset</h3>
                  <p className="text-[11px] text-purple-100">Update hardware specifications and assignment</p>
                </div>
              </div>

              <button
                onClick={() => setEditingAsset(null)}
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-xs"
              >
                ✕
              </button>
            </div>

            <form onSubmit={onEditSubmit} className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3.5">
              {/* Asset Name */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Asset Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all shadow-2xs"
                  value={editAssetForm.name}
                  onChange={(e) => setEditAssetForm({ ...editAssetForm, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl p-3 text-xs md:text-sm text-slate-800 focus:outline-none transition-all shadow-2xs resize-none"
                  value={editAssetForm.description}
                  onChange={(e) => setEditAssetForm({ ...editAssetForm, description: e.target.value })}
                />
              </div>

              {/* Asset Type Selectable Cards */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Asset Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'laptop', label: 'Laptop' },
                    { value: 'desktop', label: 'Desktop' },
                    { value: 'mobile', label: 'Mobile' },
                    { value: 'monitor', label: 'Monitor' },
                    { value: 'printer', label: 'Printer' },
                    { value: 'other', label: 'Other' }
                  ].map((t) => (
                    <div
                      key={t.value}
                      onClick={() => setEditAssetForm({ ...editAssetForm, asset_type: t.value })}
                      className={`p-2 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-2 ${
                        editAssetForm.asset_type === t.value
                          ? 'border-purple-600 bg-purple-50/40 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <span className="flex-shrink-0">{getAssetIcon(t.value)}</span>
                      <span className="font-bold text-xs text-slate-800">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value & Serial Number */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Value (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all shadow-2xs"
                    value={editAssetForm.value}
                    onChange={(e) => setEditAssetForm({ ...editAssetForm, value: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all shadow-2xs font-mono"
                    value={editAssetForm.serial_number || ''}
                    onChange={(e) => setEditAssetForm({ ...editAssetForm, serial_number: e.target.value })}
                  />
                </div>
              </div>

              {/* Assigned To User */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Assigned User (Optional)
                </label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 focus:border-purple-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all"
                  value={editAssetForm.assigned_to || ''}
                  onChange={(e) => setEditAssetForm({ ...editAssetForm, assigned_to: e.target.value })}
                >
                  <option value="">None (Available in Inventory)</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                  ))}
                </select>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setEditingAsset(null)}
                  className="px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsManagement;
